// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { Cpu, HardDrive, Zap, Clock, Download } from "lucide-react";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";

const PerformanceReports = () => {
  const [timeRange, setTimeRange] = useState("24h");

  // Sample performance data
  const generatePerformanceData = () => {
    const data = [];
    const now = new Date();

    for (let i = 23; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      data.push({
        time: time.toLocaleTimeString("de-DE", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        cpu: Math.random() * 30 + 20,
        memory: Math.random() * 40 + 30,
        responseTime: Math.random() * 200 + 50,
        throughput: Math.random() * 1000 + 500,
      });
    }
    return data;
  };

  const [performanceData] = useState(generatePerformanceData());

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="Performance Reports"
        subtitle="System performance metrics and analysis"
        status="Live Data"
        statusColor="green"
        ranges={["Last Hour", "Last 24 Hours", "Last 7 Days", "Last 30 Days"]}
        selectedRange={
          timeRange === "1h"
            ? "Last Hour"
            : timeRange === "24h"
              ? "Last 24 Hours"
              : timeRange === "7d"
                ? "Last 7 Days"
                : "Last 30 Days"
        }
        onRangeChange={(value) => {
          const rangeMap = {
            "Last Hour": "1h",
            "Last 24 Hours": "24h",
            "Last 7 Days": "7d",
            "Last 30 Days": "30d",
          };
          setTimeRange(rangeMap[value]);
        }}
        buttons={[
          {
            text: "Export Report",
            icon: <Download className="h-4 w-4" />,
            // TODO: wire up export — no backend endpoint yet
            onClick: () => {},
            className:
              "bg-primary-color text-white px-4 py-2 rounded-md hover:bg-primary-color-dark flex items-center gap-2",
          },
        ]}
      />

      <div className="mx-auto px-4 lg:px-4 py-4">
        {/* Performance Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md dark:border dark:border-slate-700 p-6">
            <div className="flex items-center">
              <Cpu className="h-8 w-8 text-blue-500 dark:text-blue-400 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-slate-400">
                  CPU Usage
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                  24.3%
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md dark:border dark:border-slate-700 p-6">
            <div className="flex items-center">
              <HardDrive className="h-8 w-8 text-primary-color dark:text-primary-color/40 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-slate-400">
                  Memory Usage
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                  68.1%
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md dark:border dark:border-slate-700 p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-primary-color dark:text-primary-color/40 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-slate-400">
                  Response Time
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                  156ms
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md dark:border dark:border-slate-700 p-6">
            <div className="flex items-center">
              <Zap className="h-8 w-8 text-primary-color dark:text-primary-color/40 mr-3" />
              <div>
                <div className="text-sm font-medium text-gray-600 dark:text-slate-400">
                  Throughput
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                  842 req/s
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md dark:border dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4">
              CPU & Memory Usage
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="cpu"
                    stackId="1"
                    stroke="#3b82f6"
                    fill="#3b82f6"
                    fillOpacity={0.6}
                  />
                  <Area
                    type="monotone"
                    dataKey="memory"
                    stackId="1"
                    stroke="#10b981"
                    fill="#10b981"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md dark:border dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4">
              Response Time & Throughput
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="responseTime"
                    stroke="#f59e0b"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="throughput"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceReports;
